/**
 * Alias display resolution utilities.
 *
 * Pure functions for resolving pilot display names and import IDs
 * when alias records (parent_id) are involved.
 *
 * Requirements: 8.12, 8.13
 */

/**
 * Resolve the display name for a pilot record.
 *
 * If the pilot has a `parent_id` (i.e. is an alias), the parent pilot's
 * name is returned for display. Otherwise the pilot's own name is returned.
 *
 * The `pilotsMap` is a Map of pilot UUID → pilot record, used to look up
 * the parent. If the parent cannot be found in the map, the alias's own
 * name is returned as a fallback.
 *
 * @param {Object} pilot - A pilot record (must have `id`, `name`, and optionally `parent_id`)
 * @param {Map<string, Object>} pilotsMap - Map of pilot UUID → pilot record
 * @returns {string} The resolved display name
 */
export function resolveDisplayName(pilot, pilotsMap) {
  if (!pilot || typeof pilot !== 'object') return '';

  // Not an alias — return own name
  if (!pilot.parent_id) return pilot.name || '';

  // Alias — look up parent
  const parent = pilotsMap.get(pilot.parent_id);
  if (parent && parent.name) return parent.name;

  // Fallback: parent not found in map, return own name
  return pilot.name || '';
}

/**
 * Resolve a pilot ID for import purposes.
 *
 * When an import process matches a name or email to an alias record,
 * this function returns the parent user's UUID so that new records
 * are created against the parent rather than the alias.
 *
 * If the matched pilot is not an alias (no `parent_id`), the pilot's
 * own ID is returned unchanged.
 *
 * @param {Object} pilot - The matched pilot record (must have `id` and optionally `parent_id`)
 * @returns {string} The resolved pilot UUID (parent's ID for aliases, own ID otherwise)
 */
export function resolveImportPilotId(pilot) {
  if (!pilot || typeof pilot !== 'object') return null;
  return pilot.parent_id || pilot.id;
}
